import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Trophy, Target, Warning, CheckCircle, XCircle, Clock, Fire } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

function fmt(n, d = 2) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number(n).toLocaleString("en-US", { maximumFractionDigits: d });
}

export default function PropFirm() {
  const [firms, setFirms] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [selectedFirm, setSelectedFirm] = useState("");
  const [selectedChallenge, setSelectedChallenge] = useState("");
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/prop/firms").then(r => setFirms(r.data)).catch(() => {});
    api.get("/accounts").then(r => {
      setAccounts(r.data);
      // If any account already has a prop challenge, load its progress
      const withChallenge = r.data.find(a => a.prop_challenge);
      if (withChallenge) {
        setSelectedAccount(withChallenge.id);
        loadProgress(withChallenge.id);
      } else if (r.data.length) {
        setSelectedAccount(r.data[0].id);
      }
    }).catch(() => {});
  }, []);

  const loadProgress = async (accId) => {
    try {
      const { data } = await api.get(`/prop/progress/${accId}`);
      setProgress(data);
    } catch (e) {
      setProgress(null);
    }
  };

  const attach = async () => {
    if (!selectedAccount || !selectedFirm || !selectedChallenge) {
      toast.error("Pick account, firm and challenge");
      return;
    }
    setLoading(true);
    try {
      await api.post("/prop/attach", {
        account_id: selectedAccount, firm_id: selectedFirm, challenge_id: selectedChallenge,
      });
      toast.success("Prop challenge attached");
      await loadProgress(selectedAccount);
      // refresh accounts
      const { data } = await api.get("/accounts"); setAccounts(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Attach failed");
    } finally { setLoading(false); }
  };

  const detach = async () => {
    if (!window.confirm("Detach the prop challenge from this account? (Trades stay.)")) return;
    try {
      await api.post("/prop/detach", { account_id: selectedAccount });
      setProgress(null);
      const { data } = await api.get("/accounts"); setAccounts(data);
      toast.success("Detached");
    } catch { toast.error("Detach failed"); }
  };

  const firm = firms.find(f => f.id === selectedFirm);
  const challenge = firm?.challenges.find(c => c.id === selectedChallenge);
  const active = progress?.prop_challenge;

  const targetPct = active?.rules?.profit_target_pct || 0;
  const progressPct = progress ? Math.min(100, Math.max(0, (progress.pct_return / targetPct) * 100)) : 0;
  const ddPct = progress?.max_drawdown_pct || 0;
  const maxDdPct = active?.rules?.max_total_drawdown_pct || 1;
  const ddRatio = Math.min(100, (ddPct / maxDdPct) * 100);
  const dayRatio = active ? Math.min(100, (progress?.days_traded || 0) / (active.rules.min_trading_days || 1) * 100) : 0;
  const dailyLossPct = progress?.today_pnl_pct || 0;
  const dailyLossRatio = active ? Math.min(100, (Math.abs(Math.min(0, dailyLossPct)) / (active.rules.max_daily_loss_pct || 1)) * 100) : 0;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="propfirm-page">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500">
          <Trophy size={20} weight="fill" className="text-white" />
        </span>
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Challenge tracker</p>
          <h1 className="font-display text-3xl font-bold tracking-tight">Prop Firm</h1>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mt-2 mb-6 max-w-2xl">
        Attach a prop-firm challenge to any account and Tradelog tracks profit target, daily loss,
        drawdown, days traded and deadline in real time. Trades are pulled from that account automatically.
      </p>

      {/* Attach card */}
      {!active && (
        <div className="rounded-2xl border border-border bg-card p-6" data-testid="attach-card">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Start tracking</p>
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Account</label>
              <select value={selectedAccount} onChange={e => setSelectedAccount(e.target.value)}
                className="mt-1 w-full h-10 rounded-md bg-secondary border-0 px-3 text-sm"
                data-testid="attach-account-select">
                <option value="">— pick —</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name} · {a.broker}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Prop Firm</label>
              <select value={selectedFirm} onChange={e => { setSelectedFirm(e.target.value); setSelectedChallenge(""); }}
                className="mt-1 w-full h-10 rounded-md bg-secondary border-0 px-3 text-sm"
                data-testid="attach-firm-select">
                <option value="">— pick —</option>
                {firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Challenge</label>
              <select value={selectedChallenge} onChange={e => setSelectedChallenge(e.target.value)}
                disabled={!firm}
                className="mt-1 w-full h-10 rounded-md bg-secondary border-0 px-3 text-sm disabled:opacity-50"
                data-testid="attach-challenge-select">
                <option value="">— pick —</option>
                {firm?.challenges.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
          </div>
          {challenge && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
              <RuleTile label="Start balance"     val={`$${fmt(challenge.start_balance, 0)}`} />
              <RuleTile label="Profit target"     val={`${challenge.rules.profit_target_pct}%`} />
              <RuleTile label="Max daily loss"    val={`${challenge.rules.max_daily_loss_pct}%`} />
              <RuleTile label="Max drawdown"      val={`${challenge.rules.max_total_drawdown_pct}%`} />
              <RuleTile label="Min days"          val={`${challenge.rules.min_trading_days}`} />
            </div>
          )}
          <button onClick={attach} disabled={loading || !selectedAccount || !selectedChallenge}
            className="mt-5 h-10 px-5 rounded-full bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50"
            data-testid="attach-btn">
            {loading ? "Attaching…" : "Attach challenge"}
          </button>
        </div>
      )}

      {/* Active challenge dashboard */}
      {active && progress && (
        <>
          <div className="rounded-2xl border border-border bg-card p-6 flex flex-wrap items-center gap-6" data-testid="active-header">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Firm · Challenge</p>
              <p className="font-display text-xl font-bold mt-1" data-testid="active-firm-label">{active.firm_name} · {active.challenge_label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{active.phase} · Started {active.started_at?.slice(0,10)}</p>
            </div>
            <div className="ml-auto flex items-center gap-3">
              <StatusPill status={progress.computed_status} />
              <button onClick={detach} className="text-xs text-red-500 hover:underline" data-testid="detach-btn">Detach</button>
            </div>
          </div>

          {/* Rule progress cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <ProgressCard title="Profit target"
              value={`${progress.pct_return}%`} target={`of ${targetPct}%`}
              pct={progressPct} tone={progress.pct_return >= 0 ? "green" : "red"}
              subtitle={`$${fmt(progress.total_pnl)} of $${fmt(progress.limits.profit_target)}`}
              testid="card-target" />
            <ProgressCard title="Max drawdown"
              value={`${progress.max_drawdown_pct}%`} target={`of ${maxDdPct}% max`}
              pct={ddRatio} tone={ddRatio >= 80 ? "red" : ddRatio >= 60 ? "amber" : "green"}
              subtitle={`from ${active.rules.hwm_drawdown ? "high-watermark" : "start balance"}`}
              testid="card-drawdown" />
            <ProgressCard title="Today's P&L"
              value={`${progress.today_pnl_pct}%`} target={`limit ${active.rules.max_daily_loss_pct}%`}
              pct={dailyLossRatio} tone={dailyLossRatio >= 80 ? "red" : dailyLossRatio >= 60 ? "amber" : "green"}
              subtitle={`$${fmt(progress.today_pnl)} today`}
              testid="card-daily" />
            <ProgressCard title="Days traded"
              value={`${progress.days_traded}`} target={`of ${active.rules.min_trading_days} min`}
              pct={dayRatio} tone={dayRatio >= 100 ? "green" : "amber"}
              subtitle={progress.days_left != null ? `${progress.days_left} days left` : "no deadline"}
              testid="card-days" />
          </div>

          {/* Warnings / violations */}
          {(progress.violations.length > 0 || progress.warnings.length > 0) && (
            <div className="mt-4 space-y-2">
              {progress.violations.map((v, i) => (
                <div key={i} className="rounded-xl border border-red-500/30 bg-red-500/5 p-3 flex items-center gap-2 text-sm text-red-500"
                  data-testid={`violation-${i}`}>
                  <XCircle size={18} weight="fill" /> {v}
                </div>
              ))}
              {progress.warnings.map((w, i) => (
                <div key={i} className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 flex items-center gap-2 text-sm text-amber-500"
                  data-testid={`warning-${i}`}>
                  <Warning size={18} weight="fill" /> {w}
                </div>
              ))}
            </div>
          )}

          {/* Equity curve */}
          <div className="mt-4 rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Equity curve</p>
              <p className="text-sm">
                <span className="text-muted-foreground">Current</span>{" "}
                <span className="font-num font-bold">${fmt(progress.current_equity)}</span>{" "}
                <span className={progress.total_pnl >= 0 ? "text-emerald-500" : "text-red-500"}>
                  {progress.total_pnl >= 0 ? "+" : ""}${fmt(progress.total_pnl)} ({progress.pct_return >= 0 ? "+" : ""}{progress.pct_return}%)
                </span>
              </p>
            </div>
            <div className="h-64" data-testid="equity-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={progress.equity_curve}>
                  <XAxis dataKey="date" stroke="#71717a" fontSize={10} />
                  <YAxis domain={["auto", "auto"]} stroke="#71717a" fontSize={10}
                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => `$${fmt(v)}`} labelFormatter={(l) => `Date: ${l}`}
                    contentStyle={{ background: "#111", border: "1px solid #333", borderRadius: 8 }} />
                  <ReferenceLine y={active.start_balance} stroke="#71717a" strokeDasharray="3 3" label={{ value: "start", position: "insideLeft", fill: "#71717a", fontSize: 10 }} />
                  <ReferenceLine y={active.start_balance * (1 + targetPct / 100)} stroke="#22c55e" strokeDasharray="3 3" label={{ value: "target", position: "insideRight", fill: "#22c55e", fontSize: 10 }} />
                  <ReferenceLine y={active.start_balance * (1 - maxDdPct / 100)} stroke="#ef4444" strokeDasharray="3 3" label={{ value: "max DD", position: "insideRight", fill: "#ef4444", fontSize: 10 }} />
                  <Line type="monotone" dataKey="equity" stroke="#3b82f6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function RuleTile({ label, val }) {
  return (
    <div className="rounded-lg border border-border/60 bg-secondary/40 px-3 py-2">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="font-num text-base font-bold mt-0.5">{val}</p>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    passed: { icon: CheckCircle, color: "bg-emerald-500/15 text-emerald-500", label: "PASSED" },
    failed: { icon: XCircle,     color: "bg-red-500/15 text-red-500",         label: "FAILED" },
    active: { icon: Fire,        color: "bg-blue-500/15 text-blue-500",       label: "IN PROGRESS" },
  };
  const s = map[status] || map.active;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold ${s.color}`} data-testid="status-pill">
      <Icon size={12} weight="fill" /> {s.label}
    </span>
  );
}

function ProgressCard({ title, value, target, pct, tone, subtitle, testid }) {
  const toneMap = {
    green: "bg-emerald-500",
    amber: "bg-amber-500",
    red: "bg-red-500",
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{title}</p>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="font-num text-3xl font-bold">{value}</span>
        <span className="text-xs text-muted-foreground">{target}</span>
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-secondary overflow-hidden">
        <div className={`h-full ${toneMap[tone]}`} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
      </div>
      {subtitle && <p className="text-[11px] text-muted-foreground mt-2">{subtitle}</p>}
    </div>
  );
}
